/**
 * Widget Type Definitions
 * Types for the widget dashboard system
 */

import type { BaseEntity } from '../models/common';

/**
 * Available widget types
 */
export enum WidgetType {
  CLOCK = 'clock',
  WORLD_CLOCK = 'world-clock',
  TIMER = 'timer',
  STOPWATCH = 'stopwatch',
  ALARM = 'alarm',
}

/**
 * Widget size configuration
 */
export interface WidgetSize {
  w: number; // width in grid units
  h: number; // height in grid units
  minW?: number; // minimum width
  minH?: number; // minimum height
  maxW?: number; // maximum width
  maxH?: number; // maximum height
}

/**
 * Widget position in the grid
 */
export interface WidgetPosition {
  x: number; // column position
  y: number; // row position
  w: number; // width in grid units
  h: number; // height in grid units
}

/**
 * Base widget configuration (shared by all widgets)
 */
export interface BaseWidgetConfig extends BaseEntity {
  type: WidgetType;
  name: string; // user-defined widget name
  position: WidgetPosition;
}

/**
 * Clock widget configuration
 */
export interface ClockWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.CLOCK;
  settings: {
    format: '12h' | '24h';
    showSeconds: boolean;
    showDate: boolean;
    displayType: 'analog' | 'digital';
    theme?: string; // clock face theme
  };
}

/**
 * World clock widget configuration
 */
export interface WorldClockWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.WORLD_CLOCK;
  settings: {
    timezones: Array<{
      timezone: string; // IANA timezone identifier
      label: string; // custom label
    }>;
    format: '12h' | '24h';
    showSeconds: boolean;
  };
}

/**
 * Timer widget configuration
 */
export interface TimerWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.TIMER;
  settings: {
    duration: number; // duration in seconds
    autoStart: boolean;
    sound?: string; // alarm sound identifier
  };
  state?: {
    remaining: number; // remaining time in seconds
    isRunning: boolean;
    isPaused: boolean;
  };
}

/**
 * Stopwatch widget configuration
 */
export interface StopwatchWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.STOPWATCH;
  settings: {
    showMilliseconds: boolean;
    lapRecording: boolean;
  };
  state?: {
    elapsed: number; // elapsed time in milliseconds
    isRunning: boolean;
    laps?: number[]; // lap times in milliseconds
  };
}

/**
 * Alarm widget configuration
 */
export interface AlarmWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.ALARM;
  settings: {
    alarms: Array<{
      id: string;
      time: string; // HH:mm format
      days: number[]; // 0-6 (Sunday-Saturday)
      enabled: boolean;
      label: string;
      sound?: string;
    }>;
  };
}

/**
 * Union type of all widget configurations
 */
export type WidgetConfig =
  | ClockWidgetConfig
  | WorldClockWidgetConfig
  | TimerWidgetConfig
  | StopwatchWidgetConfig
  | AlarmWidgetConfig;

/**
 * Dashboard configuration
 */
export interface DashboardConfig {
  widgets: WidgetConfig[];
  layout: {
    cols: number; // number of columns
    rowHeight: number; // height of each row in pixels
    margin: [number, number]; // [horizontal, vertical] margin
  };
  theme: 'light' | 'dark' | 'auto';
}

/**
 * Widget creation payload (used when adding a new widget)
 */
export interface CreateWidgetPayload {
  type: WidgetType;
  name: string;
  position?: Partial<WidgetPosition>; // optional, can be auto-calculated
}

/**
 * Widget update payload (used when modifying a widget)
 */
export interface UpdateWidgetPayload {
  id: string;
  name?: string;
  position?: WidgetPosition;
  settings?: Record<string, unknown>;
}
